<div class="bg-white shadow rounded-lg p-6">
  <h2 class="text-xl font-semibold text-gray-900 mb-4">Datei hochladen</h2>

  <!-- Messages -->
  @if (successMessage()) {
    <div class="rounded-md bg-green-50 p-4 mb-4">
      <p class="text-sm font-medium text-green-800">{{ successMessage() }}</p>
    </div>
  }

  @if (errorMessage()) {
    <div class="rounded-md bg-red-50 p-4 mb-4">
      <p class="text-sm font-medium text-red-800">{{ errorMessage() }}</p>
    </div>
  }

  <form (ngSubmit)="uploadFile()" class="space-y-4">
    <!-- Category Selection with Chips -->
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-2">
        Kategorie *
      </label>
      <div class="flex gap-2">
        @for (cat of categories; track cat.value) {
          <button
            type="button"
            (click)="selectCategory(cat.value)"
            [class]="selectedCategory() === cat.value
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'"
            class="px-4 py-2 rounded-full border-2 font-medium text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            {{ cat.label }}
          </button>
        }
      </div>
    </div>

    <!-- File Input -->
    <div>
      <label for="fileInput" class="block text-sm font-medium text-gray-700">
        Datei (PDF oder Excel) *
      </label>
      <input
        type="file"
        id="fileInput"
        accept="application/pdf,.pdf,application/vnd.ms-excel,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx"
        (change)="onFileSelected($event)"
        class="mt-1 block w-full text-sm text-gray-500
          file:mr-4 file:py-2 file:px-4
          file:rounded-md file:border-0
          file:text-sm file:font-semibold
          file:bg-blue-50 file:text-blue-700
          hover:file:bg-blue-100"
      />
      @if (selectedFile()) {
        <p class="mt-2 text-sm text-gray-600">
          Ausgewählt: {{ selectedFile()!.name }} ({{ (selectedFile()!.size / 1024 / 1024).toFixed(2) }} MB)
        </p>
      }
    </div>

    <!-- Title -->
    <div>
      <label for="title" class="block text-sm font-medium text-gray-700">
        Titel *
      </label>
      <input
        type="text"
        id="title"
        name="title"
        [(ngModel)]="fileTitle"
        required
        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
        placeholder="Titel der Datei"
      />
    </div>

    <!-- Description -->
    <div>
      <label for="description" class="block text-sm font-medium text-gray-700">
        Beschreibung (optional)
      </label>
      <textarea
        id="description"
        name="description"
        [(ngModel)]="fileDescription"
        rows="3"
        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
        placeholder="Zusätzliche Informationen"
      ></textarea>
    </div>

    <!-- Submit Button -->
    <div>
      <button
        type="submit"
        [disabled]="isUploading() || !selectedFile()"
        class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        @if (isUploading()) {
          <span>Wird hochgeladen...</span>
        } @else {
          <span>Hochladen</span>
        }
      </button>
    </div>
  </form>
</div>

<!-- Existing Files -->
<div class="bg-white shadow rounded-lg p-6 mt-6">
  <h2 class="text-xl font-semibold text-gray-900 mb-4">
    Vorhandene Dateien:
    @if (selectedCategory() === 'ausschreibungen') {
      <span class="text-blue-600">Ausschreibungen</span>
    } @else {
      <span class="text-blue-600">Ergebnisse</span>
    }
  </h2>

  @if (isLoadingFiles()) {
    <div class="text-center py-8">
      <p class="text-gray-500">Lädt Dateien...</p>
    </div>
  } @else {
    @if (selectedCategory() === 'ausschreibungen') {
      <!-- Ausschreibungen -->
      @if (ausschreibungenFiles().length === 0) {
        <p class="text-gray-500 text-sm">Keine Dateien vorhanden</p>
      } @else {
        <div class="space-y-2">
          @for (file of ausschreibungenFiles(); track file.Id) {
            <div class="flex justify-between items-center p-3 bg-gray-50 rounded hover:bg-gray-100">
              <div class="flex-1">
                <p class="font-medium text-gray-900">{{ file.Title }}</p>
                @if (file.Description) {
                  <p class="text-sm text-gray-600">{{ file.Description }}</p>
                }
                <p class="text-xs text-gray-500 mt-1">{{ file.FileName }}</p>
              </div>
              <div class="flex gap-2 ml-4">
                <a
                  [href]="'ausschreibungen/' + file.FileName"
                  target="_blank"
                  class="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                  Öffnen
                </a>
                <button
                  (click)="confirmDelete('ausschreibungen', file.FileName)"
                  class="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                >
                  Löschen
                </button>
              </div>
            </div>
          }
        </div>
      }
    } @else {
      <!-- Ergebnisse -->
      @if (ergebnisseFiles().length === 0) {
        <p class="text-gray-500 text-sm">Keine Dateien vorhanden</p>
      } @else {
        <div class="space-y-2">
          @for (file of ergebnisseFiles(); track file.Id) {
            <div class="flex justify-between items-center p-3 bg-gray-50 rounded hover:bg-gray-100">
              <div class="flex-1">
                <p class="font-medium text-gray-900">{{ file.Title }}</p>
                @if (file.Description) {
                  <p class="text-sm text-gray-600">{{ file.Description }}</p>
                }
                <p class="text-xs text-gray-500 mt-1">{{ file.FileName }}</p>
              </div>
              <div class="flex gap-2 ml-4">
                <a
                  [href]="'ergebnisse/' + file.FileName"
                  target="_blank"
                  class="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                  Öffnen
                </a>
                <button
                  (click)="confirmDelete('ergebnisse', file.FileName)"
                  class="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                >
                  Löschen
                </button>
              </div>
            </div>
          }
        </div>
      }
    }
  }
</div>

<!-- Delete Confirmation Modal -->
@if (showDeleteConfirm()) {
  <div class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
    <div class="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
      <!-- Modal Header -->
      <div class="px-6 py-4 border-b border-gray-200">
        <h3 class="text-lg font-semibold text-gray-900">Datei löschen</h3>
      </div>

      <!-- Modal Body -->
      <div class="px-6 py-4">
        <p class="text-gray-700">
          Möchten Sie die Datei wirklich löschen?
        </p>
        <p class="mt-2 text-sm font-semibold text-gray-900 break-words">
          "{{ fileToDelete()?.filename }}"
        </p>
        <p class="text-sm text-gray-500 mt-2">
          Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
      </div>

      <!-- Modal Footer -->
      <div class="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
        <button
          (click)="cancelDelete()"
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Abbrechen
        </button>
        <button
          (click)="deleteFile()"
          class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        >
          Löschen
        </button>
      </div>
    </div>
  </div>
}
